import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { PORTAL_URL } from "@/config/env";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthCard } from "@/components/AuthCard";

interface LocationState {
  from?: string;
}

interface LoginResponse {
  accessToken: string;
  expiresAt: string;
  user: { id: string; email: string; role: string };
}

function isValidLoginResponse(value: unknown): value is LoginResponse {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.accessToken !== "string" || v.accessToken.length === 0) return false;
  if (typeof v.expiresAt !== "string") return false;
  if (!Number.isFinite(new Date(v.expiresAt).getTime())) return false;
  if (typeof v.user !== "object" || v.user === null) return false;
  const u = v.user as Record<string, unknown>;
  return typeof u.id === "string" && typeof u.email === "string" && typeof u.role === "string";
}

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from ?? "/";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const res = await fetch(`${PORTAL_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        // Generisch — das Portal leakt nie WARUM (unbekannte Email / falsches Passwort / gesperrt).
        setError("Login fehlgeschlagen");
        return;
      }

      const data: unknown = await res.json();
      if (!isValidLoginResponse(data)) {
        setError("Ungueltige Server-Antwort");
        return;
      }
      const expiresAtMs = new Date(data.expiresAt).getTime();
      if (expiresAtMs <= Date.now()) {
        setError("Token bereits abgelaufen (Server-Uhr?)");
        return;
      }
      setSession({
        accessToken: data.accessToken,
        expiresAt: expiresAtMs,
        user: data.user,
      });
      navigate(from, { replace: true });
    } catch {
      // Netz-/CORS-Fehler → gleiche generische Meldung, kein Detail-Leak.
      setError("Login fehlgeschlagen");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthCard title="License Portal" description="Vendor-Konsole — bitte anmelden.">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            data-testid="email-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Passwort</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isSubmitting}
            data-testid="password-input"
          />
        </div>
        {error && (
          <div
            className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger"
            role="alert"
            data-testid="login-error"
          >
            {error}
          </div>
        )}
        <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="submit-button">
          {isSubmitting ? "Anmelden…" : "Anmelden"}
        </Button>
      </form>
    </AuthCard>
  );
}
